import { Component, Input } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { CartQuickOrderFormComponent } from '@spartacus/cart/quick-order/components'
import { Observable } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, first, map, switchMap, tap } from 'rxjs/operators'
import {
  Config,
  EventService,
  GlobalMessageService,
  GlobalMessageType,
  Product,
  ProductSearchConnector
} from '@spartacus/core'
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap'
import { ActiveCartService } from '@spartacus/cart/base/core'
import { CartAddEntryFailEvent, CartAddEntrySuccessEvent } from '@spartacus/cart/base/root'
import { DataAnalyticsClickSource, DataAnalyticsEventLabel, maxQuantityFallback } from '../../../shared/mb-constants'
import { MbAddToCartService } from '../../../shared/services/mb-add-to-cart.service'

@Component({
  selector: 'mb-quick-add-to-cart',
  templateUrl: './quick-add-to-cart.component.html',
  styleUrls: ['./quick-add-to-cart.component.scss']
})
export class QuickAddToCartComponent extends CartQuickOrderFormComponent {

  @Input()
  max = +(this.config.context?.maxAllowedQuantityPerProductInCart || maxQuantityFallback)
  @Input()
  step = 1

  formConfig = this.config?.quickOrder?.searchForm

  constructor (protected activeCartService: ActiveCartService,
              protected eventService: EventService,
              protected formBuilder: UntypedFormBuilder,
              protected globalMessageService: GlobalMessageService,
              protected productSearch: ProductSearchConnector,
              protected mbAddToCartService: MbAddToCartService,
              protected config: Config) {
    super(activeCartService, eventService, formBuilder, globalMessageService)
  }

  applyQuickOrder (): void {
    if (this.quickOrderForm.invalid) {
      this.quickOrderForm.markAllAsTouched()
      return
    }

    const productCode = this.quickOrderForm.get('productCode')?.value
    const quantity = this.quickOrderForm.get('quantity')?.value

    this.watchAddEntrySuccessEvent()
    this.watchAddEntryFailEvent()

    if (productCode && quantity) {
      this.mbAddToCartService.addToCart({
        productCode,
        quantity,
        source: DataAnalyticsClickSource.QUICK_ADD,
        eventLabel: DataAnalyticsEventLabel.QUICK_ADD
      })
    }
  }

  protected buildForm () {
    this.quickOrderForm = this.formBuilder.group({
      productCode: ['', [
        Validators.required,
        Validators.pattern(/^[0-9]{3}-([0-9]{2}|[0-9]{3}-[0-9]{3})$/),
        Validators.maxLength(11)
      ]],
      quantity: [
        this.minQuantityValue,
        {
          validators: [
            Validators.required,
            Validators.min(this.min),
            Validators.max(this.max)
          ]
        },
      ],
    })
  }

  get productCode () {
    return this.quickOrderForm.get('productCode')?.value
  }

  set productCode (productCode: string | undefined) {
    if (productCode) {
      this.quickOrderForm.get('productCode')?.setValue(productCode)
    }
  }

  get quantity () {
    return this.quickOrderForm.get('quantity')?.value
  }

  set quantity (value: number) {
    this.quickOrderForm.get('quantity')?.setValue(value)
  }

  protected getValidCount (value: number) {
    const v = super.getValidCount(value)
    return v > this.max && this.max || v
  }

  onSelect (event: NgbTypeaheadSelectItemEvent<Product>) {
    event.preventDefault()
    this.productCode = event.item.code
  }

  search = (text$: Observable<string>) => text$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(term => !term.length && this.quickOrderForm.get('productCode')?.markAsPristine()),
    filter(term => term.length > (this.formConfig?.minCharactersBeforeRequest || 2)),
    switchMap(term => this.productSearch.search(term).pipe(
      map(r => r.products?.flatMap(p => p.variantOptions)),
      map(ps => ps?.slice(0, this.formConfig?.maxProducts || 10)),
    )),
  )

  protected watchAddEntrySuccessEvent (): void {
    this.cartEventsSubscription.add(
      this.eventService
        .get(CartAddEntrySuccessEvent)
        .pipe(first())
        .subscribe(data => {
          let key = 'quickOrderCartForm.stockLevelReached'
          let productTranslation
          let messageType = GlobalMessageType.MSG_TYPE_WARNING
          if (data.quantityAdded) {
            key = data.quantityAdded > 1 && 'quickOrderCartForm.entriesWereAdded' || 'quickOrderCartForm.entryWasAdded'
            productTranslation = data.quantityAdded > 1 && 'quickOrderCartForm.products' || 'quickOrderCartForm.product'
            messageType = GlobalMessageType.MSG_TYPE_CONFIRMATION
          }
          this.globalMessageService.add({
            key,
            params: {
              product: data?.entry?.product?.name?.replace(/<[^>]*>/g, '') || productTranslation,
              quantity: data.quantityAdded,
            },
          },
          messageType,
          6000)
          this.resetForm()
        })
    )
  }

  protected watchAddEntryFailEvent (): void {
    this.cartEventsSubscription.add(
      this.eventService
        .get(CartAddEntryFailEvent)
        .pipe(first())
        .subscribe(() => {
          this.quickOrderForm.get('productCode')?.markAsPristine()
          this.globalMessageService.add(
            {
              key: 'quickOrderCartForm.noResults',
            },
            GlobalMessageType.MSG_TYPE_ERROR
          )

        })
    )
  }
}
